import { useCallback, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { message, Table, Button } from 'antd'
import get from 'lodash/get'
import mapValues from 'lodash/mapValues'
import { createUseStyles } from 'react-jss'
import { ListError, PageHeader } from '../Common'
import Filters from '../Forms/Filters'
import { formatMedicalProvider } from '../Forms/MedicalProviderSelect'
import api from '../../api'
import useQueryParams from '../../hooks/useQueryParams'
import { useAsync } from '../../hooks'
import {
  fetchUsers,
  useFetchUsers,
  useUsers,
  useUsersPagination,
} from '../../redux/slices/users'
import { replaceEntityFields } from '../../redux/slices/entities'

import { updateProvider } from '../../redux/slices/practices'
import useColumns from './useColumns'
import AlertProvidersModal from './AlertProvidersModal'

const useStyles = createUseStyles({
  center: {
    textAlign: 'center',
  },
})

const QUERY_PARAMS_SCHEMA = {
  page: Number,
  practiceId: String,
  showInactive: Boolean,
}

// Rails returns field errors as an object ({ field: [messages] }); flatten it
// to a readable string — message.error renders objects as "[object Object]".
function showUpdateError(e) {
  const errors = get(e, 'response.errors')
  const description = errors
    ? Object.values(mapValues(errors, fieldErrors => fieldErrors[0])).join('. ')
    : 'Oops, we had a problem. Please try submitting again'
  message.error(description)
}

export default function PracticeHealthProviders() {
  const { practiceId } = useParams()
  const dispatch = useDispatch()
  const classes = useStyles()
  const navigate = useNavigate()

  const getPractice = useCallback(
    () =>
      api.practices.getPractice(practiceId).then(practice => ({
        ...practice,
        logo: practice.logoPath,
      })),
    [practiceId],
  )

  const { value: practice } = useAsync(getPractice)

  const getMedicalProviders = useCallback(
    () => api.practices.getMedicalProviders(practiceId),
    [practiceId],
  )

  const {
    value: medicalProviders,
    error: providersError,
    clearError: clearProvidersError,
  } = useAsync(getMedicalProviders)

  const medicalProviderOptions = useMemo(
    () =>
      (medicalProviders || []).map(provider => ({
        value: provider.id,
        label: formatMedicalProvider(provider),
      })),
    [medicalProviders],
  )

  const providerLabelById = useMemo(
    () =>
      medicalProviderOptions.reduce((acc, option) => {
        acc[option.value] = option.label
        return acc
      }, {}),
    [medicalProviderOptions],
  )

  // The user whose alert-provider mapping is being edited in the modal.
  const [editingUser, setEditingUser] = useState(null)

  const [conditions, setConditions] = useQueryParams({
    initialValues: { page: 1, practiceId },
    schema: QUERY_PARAMS_SCHEMA,
  })

  const { pending, error, clearError } = useAsync(
    useFetchUsers(conditions, true),
  )

  const handleChange = useCallback(
    async (id, pId, enabled) => {
      try {
        await dispatch(updateProvider(id, pId, { enabled }))
        message.success('User updated successfully')
      } catch (e) {
        message.error(
          get(
            e,
            'response.errors.authorized',
            'Oops, we had a problem. Please try submitting again',
          ),
        )
      }
    },
    [dispatch],
  )

  const handleBpAlertsChange = useCallback(
    async (userId, pId, enabled) => {
      try {
        await api.users.update(userId, {
          user: { can_receive_bp_alerts: enabled },
        })
        message.success('User updated successfully')
        await dispatch(
          fetchUsers(
            conditions.page,
            practiceId,
            conditions.showInactive,
            conditions.query,
            true,
          ),
        )
      } catch (e) {
        showUpdateError(e)
      }
    },
    [dispatch, conditions, practiceId],
  )

  const handleAlertProvidersChange = useCallback(
    async (userId, providerIds) => {
      try {
        await api.users.update(userId, {
          user: { alert_medical_provider_ids: providerIds },
        })
        dispatch(
          replaceEntityFields('users', userId, {
            alertMedicalProviderIds: providerIds,
          }),
        )
        message.success('User updated successfully')
        return true
      } catch (e) {
        showUpdateError(e)
        return false
      }
    },
    [dispatch],
  )

  const alertProviders = useMemo(
    () => ({
      openModal: setEditingUser,
      labelById: providerLabelById,
    }),
    [providerLabelById],
  )

  const handlePageChange = useCallback(
    page => setConditions({ ...conditions, page }),
    [conditions, setConditions],
  )

  const handleSubmit = useCallback(
    ({ id, query, showInactive }) => {
      setConditions({
        page: 1,
        practiceId: id,
        query,
        showInactive: showInactive || undefined,
      })
    },
    [setConditions],
  )

  const providersDone = useCallback(() => {
    navigate(`/practices/${practiceId}/edit`, { replace: true })
  }, [navigate, practiceId])

  const { pageSize, total } = useUsersPagination()
  const columns = useColumns(
    practiceId,
    handleChange,
    practice,
    handleBpAlertsChange,
    alertProviders,
  )

  const users = useUsers(practiceId)

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
      {providersError && (
        <ListError
          description={providersError.message}
          onClose={clearProvidersError}
        />
      )}
      <PageHeader
        title={`Users - ${practice ? practice.name : ''}`}
        onBack={() => navigate(-1)}
      />
      <Filters currentValues={conditions} onSubmit={handleSubmit} showSearch />
      <Table
        columns={columns}
        dataSource={users}
        loading={pending}
        pagination={{
          current: conditions.page || 1,
          pageSize,
          onChange: handlePageChange,
          total,
        }}
        rowKey="id"
      />
      <div className={classes.center}>
        <Button type="primary" onClick={providersDone}>
          Done
        </Button>
      </div>
      {editingUser && (
        <AlertProvidersModal
          key={editingUser.id}
          user={editingUser}
          options={medicalProviderOptions}
          labelById={providerLabelById}
          initialValue={editingUser.alertMedicalProviderIds || []}
          onSave={async ids => {
            const saved = await handleAlertProvidersChange(editingUser.id, ids)
            if (saved) setEditingUser(null)
          }}
          onClose={() => setEditingUser(null)}
        />
      )}
    </div>
  )
}
